/* eslint-disable jsx-a11y/media-has-caption */
import type React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useStore } from 'effector-react';
import hexRgb from 'hex-rgb';
import { useHover } from 'react-use';
import rgbHex from 'rgb-hex';
import { type GeneralID, type Video, VideoPosType } from '@types';
import { $loadedScene } from '@stores/loadedStore';
import { $sceneMapApi } from '@stores/dataStores';
// @ts-ignore
import ChromaKey from '@utils/ChromaKey';
import { getMediaUrl } from '@utils/urlMakers';
import WidgetsContainer from '@components/PlayerWidgets/WidgetsContainer';
import WidgetItem from '@components/PlayerWidgets/WidgetItem';
import WidgetRow from '@components/PlayerWidgets/WidgetRow';
import ColorPicker from '@components/ColorPicker';
import Slider from '@components/Slider';
import Button from '@components/Button';
import colorObjToString from '@utils/colorObjToString';
import colorStringToHexAndAlpha from '@utils/colorStringToHexAndAlpha';
import useToggle from '@hooks/useToggle';
import IconPicker from '@components/icons/IconPicker';
import {
  $GateRotateEditMode,
  $rotateCurrentLeft,
  $rotateIsPlaying,
} from '@stores/autoRotateStores';
import useTemplateFeature from '@hooks/useTemplateFeature';
import { PX_PER_SEC } from '../constants';

export function VHumanWidget() {
  const scene = useStore($loadedScene);
  const pickMode = useToggle(false);
  const rotateEditMode = useStore($GateRotateEditMode.state);
  const autoRotateVideo = useTemplateFeature('autoRotateVideo');
  if (!scene) return null;
  if (!autoRotateVideo) return null;
  const { video: videoList = [] } = scene;
  const video = videoList[0];
  if (!video) return null;

  if (rotateEditMode) {
    return <VideoPreview video={video} />;
  }

  return (
    <>
      <VHumanChromakey
        video={video}
        sceneId={scene.id}
        onPickClick={() => pickMode.on()}
      />
      <VHumanPoster
        video={video}
        sceneId={scene.id}
        isPickMode={pickMode.isOn}
        exitPickMode={pickMode.off}
      />
    </>
  );
}

const posMap: Record<VideoPosType, React.CSSProperties> = {
  [VideoPosType.bottomRight]: { right: 0 },
  [VideoPosType.bottomLeft]: { left: 0 },
  [VideoPosType.bottomCenter]: {},
};

const defaultMaskSimilarity = 0.11;

function VideoPreview({ video }: { video: Video }) {
  const {
    pcPosition = VideoPosType.bottomRight,
    scale = 1,
    mediaUrl,
    maskSimilarity = defaultMaskSimilarity,
    maskColor = '',
    videoThumbUrl = '',
  } = video;
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const curTime = useStore($rotateCurrentLeft) / PX_PER_SEC;
  const isPlaying = useStore($rotateIsPlaying);

  useEffect(() => {
    const ck = ChromaKey({
      video: videoRef.current,
      canvas: canvasRef.current,
      keyColor: maskColor || '#000000',
      similarity: maskColor ? maskSimilarity : 0,
      poster: getMediaUrl(videoThumbUrl),
    });
    ck.renderPoster(getMediaUrl(videoThumbUrl), true);
  }, [videoThumbUrl, maskSimilarity, maskColor]);

  useEffect(() => {
    if (!videoRef.current) return;
    if (!isPlaying) {
      videoRef.current.currentTime = curTime ?? 0;
    }
  }, [curTime]);

  useEffect(() => {
    if (!videoRef.current) return;
    if (isPlaying) {
      videoRef.current?.play();
    } else {
      videoRef.current?.pause();
    }
  }, [isPlaying]);

  return (
    <>
      <video
        ref={videoRef}
        loop={false}
        src={getMediaUrl(mediaUrl)}
        style={{ width: 0, height: 0 }}
        crossOrigin="anonymous"
      />
      <canvas
        className="absolute"
        ref={canvasRef}
        style={{
          width: 320 * scale,
          bottom: 0,
          ...posMap[pcPosition],
        }}
      />
    </>
  );
}

function VHumanChromakey({
  video,
  sceneId,
  onPickClick,
}: {
  video: Video;
  sceneId: GeneralID;
  onPickClick: () => void;
}) {
  const { maskSimilarity = defaultMaskSimilarity, maskColor = '' } = video;

  const colorString = useMemo(() => {
    if (!maskColor) return '';
    const [r, g, b] = hexRgb(maskColor, { format: 'array' });
    return colorObjToString({ r, g, b });
  }, [maskColor]);

  const handleColorStringChange = (str: string) => {
    const { hex } = colorStringToHexAndAlpha(str);
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        draft.video[0] ??= {};
        draft.video[0].maskColor = hex;
      },
    });
  };

  const handleSimilarityChange = (val: number) => {
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        draft.video[0] ??= {};
        draft.video[0].maskSimilarity = val;
      },
    });
  };

  const handleClear = () => {
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        draft.video[0] ??= {};
        draft.video[0].maskColor = '';
        draft.video[0].maskSimilarity = defaultMaskSimilarity;
      },
    });
  };

  const [btn] = useHover((isHovered) => (
    <div
      className="ml-10 flex h-30 w-30 cursor-pointer items-center justify-center rounded bg-black-#34"
      onClick={onPickClick}
    >
      <IconPicker active={isHovered} />
    </div>
  ));

  return (
    <WidgetsContainer>
      <WidgetItem title="视频抠像">
        <WidgetRow>
          <ColorPicker
            colorString={colorString}
            onChange={handleColorStringChange}
          >
            <ColorPicker.Header>选取要去除的颜色：</ColorPicker.Header>
            <div className="flex items-center">
              <ColorPicker.Bar
                className="flex-1"
                disableAlpha
                placeholder="点击选择颜色"
              />
              {btn}
            </div>
          </ColorPicker>
        </WidgetRow>
        {!!colorString && (
          <WidgetRow>
            <Slider
              min={0}
              max={1}
              value={maskSimilarity}
              onChange={handleSimilarityChange}
            >
              <Slider.Header title="相似度：" />
              <Slider.Bar step={0.01} />
              <Slider.Control step={0.01} />
            </Slider>
          </WidgetRow>
        )}
        {!!colorString && (
          <Button className="mb-10 w-full" onClick={handleClear}>
            清除设置
          </Button>
        )}
      </WidgetItem>
    </WidgetsContainer>
  );
}

const initialPos = { top: 0, left: 0, width: 0, height: 0 };

function VHumanPoster({
  video,
  sceneId,
  isPickMode,
  exitPickMode,
}: {
  video: Video;
  sceneId: GeneralID;
  isPickMode: boolean;
  exitPickMode: () => void;
}) {
  const {
    videoThumbUrl = '',
    pcPosition = VideoPosType.bottomRight,
    maskSimilarity = defaultMaskSimilarity,
    maskColor = '',
    mediaUrl = '',
  } = video;
  const ref = useRef<HTMLCanvasElement>(null);
  const pickRef = useRef<HTMLCanvasElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [pos, setPos] = useState(initialPos);
  useEffect(() => {
    const ck = ChromaKey({
      video: videoRef.current,
      canvas: ref.current,
      keyColor: maskColor || '#000000',
      similarity: maskColor ? maskSimilarity : 0,
      poster: getMediaUrl(videoThumbUrl),
    });
    ck.renderPoster(getMediaUrl(videoThumbUrl), true);
  }, [videoThumbUrl, maskSimilarity, maskColor, mediaUrl]);

  useEffect(() => {
    if (isPickMode) {
      const rect = ref.current?.getBoundingClientRect();
      if (!rect) return;
      setPos({
        top: rect.top,
        left: rect.left,
        width: rect.width,
        height: rect.height,
      });
      const ctx = pickRef.current?.getContext('2d');
      if (!ctx) return;
      const img = new Image();
      img.crossOrigin = 'Anonymous';
      img.onload = () => {
        ctx.drawImage(img, 0, 0, rect.width, rect.height);
      };
      img.src = getMediaUrl(videoThumbUrl);
    } else {
      setPos(initialPos);
    }
  }, [isPickMode]);

  const handleColorChange = (color: string) => {
    $sceneMapApi.updateScene({
      id: sceneId,
      updater: (draft) => {
        draft.video[0] ??= {};
        draft.video[0].maskColor = color;
      },
    });
    exitPickMode();
  };

  return (
    <>
      {isPickMode &&
        createPortal(
          <div className="fixed inset-0" onClick={exitPickMode}>
            <canvas
              onClick={(e) => {
                const canvas = e.target as HTMLCanvasElement;
                const res = canvas
                  .getContext('2d')
                  ?.getImageData(
                    e.pageX - pos.left,
                    e.pageY - pos.top,
                    1,
                    1
                  ).data;
                if (res) {
                  handleColorChange(`#${rgbHex(res[0], res[1], res[2])}`);
                }
              }}
              ref={pickRef}
              className="absolute cursor-crosshair"
              width={pos.width}
              height={pos.height}
              style={{
                top: pos.top,
                left: pos.left,
                boxShadow: '0 0 0 9999px rgba(0, 0, 0, 0.5)',
              }}
            />
          </div>,
          document.body
        )}
      <video
        crossOrigin="anonymous"
        ref={videoRef}
        src={getMediaUrl(mediaUrl)}
        style={{ width: 0, height: 0 }}
      />
      <canvas
        className="absolute"
        ref={ref}
        style={{
          width: 320 * (video.scale ?? 1),
          bottom: 0,
          ...posMap[pcPosition],
        }}
      />
    </>
  );
}
